/**
 * <cart-reservation-timer> — shows a countdown ("Your cart is reserved for MM:SS")
 * using sessionStorage so the countdown persists across page views within the
 * same browser session, and resets once it expires or the session ends.
 */
if (!customElements.get('cart-reservation-timer')) {
  class CartReservationTimer extends HTMLElement {
    connectedCallback() {
      this.minutes = parseInt(this.dataset.minutes, 10) || 20;
      this.storageKey = 'cartReservationExpiry';
      this.timeEl = this.querySelector('.js-cart-reservation-time');
      if (!this.timeEl) return;

      this.setExpiry();
      this.tick();
      this.interval = setInterval(this.tick.bind(this), 1000);

      this.cartChangeHandler = this.handleCartChange.bind(this);
      document.addEventListener('on:line-item:change', this.cartChangeHandler);
    }

    disconnectedCallback() {
      clearInterval(this.interval);
      document.removeEventListener('on:line-item:change', this.cartChangeHandler);
    }

    /**
     * Reads (or creates) the reservation expiry timestamp from sessionStorage.
     */
    setExpiry() {
      let expiry;

      try {
        expiry = sessionStorage.getItem(this.storageKey);
      } catch (error) {
        expiry = null;
      }

      const now = Date.now();

      if (!expiry || Number(expiry) <= now) {
        expiry = now + this.minutes * 60 * 1000;

        try {
          sessionStorage.setItem(this.storageKey, expiry);
        } catch (error) {
          // sessionStorage unavailable (e.g. private browsing) — timer still
          // works for this page view, it just won't persist across pages.
        }
      }

      this.expiry = Number(expiry);
    }

    /**
     * Adding an item to the cart nudges the reservation back out, similar to
     * how "reserved cart" timers behave on other stores.
     */
    handleCartChange(evt) {
      if (evt.detail && evt.detail.newQuantity > evt.detail.oldQuantity) {
        this.expiry = Date.now() + this.minutes * 60 * 1000;

        try {
          sessionStorage.setItem(this.storageKey, this.expiry);
        } catch (error) {
          // ignore
        }
      }
    }

    tick() {
      const remaining = this.expiry - Date.now();

      if (remaining <= 0) {
        this.setExpiry();
      }

      const totalSeconds = Math.max(0, Math.floor((this.expiry - Date.now()) / 1000));
      const mins = Math.floor(totalSeconds / 60);
      const secs = totalSeconds % 60;

      this.timeEl.textContent = `${mins}:${secs.toString().padStart(2, '0')}`;
    }
  }

  customElements.define('cart-reservation-timer', CartReservationTimer);
}
